import { ButtonCategory } from "@components/atoms/Button/types";
import { ScreenSizes } from "@src/types/screen";
import { ProductCategory } from "@utils/constants";
import currency from "currency.js";
import {
  useAnimatedScrollHandler,
  useSharedValue,
} from "react-native-reanimated";
import { useTheme } from "styled-components/native";
import { useUserCurrency } from "../../hooks/getCurrency";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useSelector } from "react-redux";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { isMeleeItem } from "@utils/product/productValidators";

export enum RequestType {
  MEMO = "memo",
  BUY = "buy",
  HOLD = "hold",
  INTERNAL = "internal",
  QUOTE = "quote",
  ORDER = "order",
}

export enum StoneType {
  SINGLE_STONES = "single_stones",
  PAIR = "pair",
  PARCELS = "parcel",
}

export const getProductCategory = (productType: ProductCategory) => {
  switch (productType) {
    case ProductCategory.DIAMOND:
      return ButtonCategory.diam;
    case ProductCategory.GEMSTONE:
      return ButtonCategory.gem;
    case ProductCategory.JEWELRY:
      return ButtonCategory.jewelry;
    case ProductCategory.LAB_GROWN_DIAMOND:
      return ButtonCategory.lab;
    default:
      return ButtonCategory.diam;
  }
};

export const requestTypes = {
  hold: {
    diamond: "bulk_hold_diamond",
    lab_grown_diamond: "bulk_hold_lab_grown_diamond",
    gemstone: "bulk_hold_gemstone",
    jewelry: "bulk_hold_jewelry",
  },
  buy: {
    diamond: "bulk_buy_diamond",
    lab_grown_diamond: "bulk_buy_lab_grown_diamond",
    gemstone: "bulk_buy_gemstone",
    jewelry: "bulk_buy_jewelry",
  },
  memo: {
    diamond: "bulk_diamond_memo",
    lab_grown_diamond: "bulk_lab_grown_diamond_memo",
    gemstone: "bulk_gemstone_memo",
    jewelry: "bulk_jewelry_memo",
  },
  quote: {
    diamond: "bulk_quote_diamond",
    lab_grown_diamond: "bulk_quote_lab_grown_diamond",
    gemstone: "bulk_quote_gemstone",
    jewelry: "bulk_quote_jewelry",
  },
  singleHoldRequest: {
    diamond: "hold_diamond",
    lab_grown_diamond: "hold_lab_grown_diamond",
    gemstone: "hold_gemstone",
    jewelry: "hold_jewelry",
  },
  singleBuyRequest: {
    diamond: "buy_diamond",
    lab_grown_diamond: "buy_lab_grown_diamond",
    gemstone: "buy_gemstone",
    jewelry: "buy_jewelry",
  },
  singleMemoRequest: {
    diamond: "diamond_memo",
    lab_grown_diamond: "lab_grown_diamond_memo",
    gemstone: "gemstone_memo",
    jewelry: "jewelry_memo",
  },
  singleQuoteRequest: {
    diamond: "quote_diamond",
    lab_grown_diamond: "quote_lab_grown_diamond",
    gemstone: "quote_gemstone",
    jewelry: "quote_jewelry",
  },
};

export const getRequestType = (str: string, t: any) => {
  switch (str) {
    case RequestType.BUY:
      return t("bmh.title.buy.text", "Buy Request");
    case RequestType.MEMO:
      return t("bmh.title.memo.text", "Memo Request");
    case RequestType.HOLD:
      return t("bmh.title.hold.text", "Hold Request");
    case RequestType.QUOTE:
      return t("bmh.title.quote.text", "Quote");
    default:
      return t("bmh.title.memo.text", "Memo Request");
  }
};

export const getPageHeaderTitle = (
  currentProductType: any,
  activeRequestType: any,
  stoneType?: any,
  t?: any
) => {
  let product = currentProductType.label;
  let mStoneType = "";
  if (stoneType === StoneType.PAIR) {
    mStoneType = t("productDetail.pair.text", "Pair");
  } else if (stoneType === StoneType.PARCELS) {
    mStoneType = t("productDetail.parcels.text", "Parcel");
  }
  return `${mStoneType ? ` ${mStoneType} ` : ` ${product} `}${getRequestType(
    activeRequestType,
    t
  )}`;
};

export const getBulkRequestPageHeaderTitle = (
  currentProductType: any,
  stoneType?: any,
  t?: any,
  count?: number
) => {
  let product = currentProductType.label;
  let mStoneType = "";
  if (stoneType === StoneType.PAIR) {
    mStoneType = t("productDetail.pair.text", "Pair");
  } else if (stoneType === StoneType.PARCELS) {
    mStoneType = t("productDetail.parcels.text", "Parcel");
  }
  return `${mStoneType ? ` ${mStoneType} ` : ` ${product} `}${t(
    "bulkListScreen.title.text"
  )}${(count ?? 0) > 0 ? ` (${count})` : ""}`;
};

export const getStoneTypeFromProductSubType = (subType?: string) => {
  if (subType === StoneType.PAIR) {
    return StoneType.PAIR;
  }
  if (subType === StoneType.PARCELS) {
    return StoneType.PARCELS;
  }
  return StoneType.SINGLE_STONES;
};

export const getStoneTypeFromSectionName = (sectionName: string) => {
  switch (sectionName) {
    case SearchFilterTypeEnum.PAIRS:
      return StoneType.PAIR;
    case SearchFilterTypeEnum.PARCEL:
      return StoneType.PARCELS;
    default:
      return StoneType.SINGLE_STONES;
  }
};

export const getStoreCustomerAttributes = (
  dynamicFormValues: any,
  formData: any,
  item?: any
) => {
  const userCurrency = useUserCurrency();
  const userInfo = useUserInfo();
  const isCustomApp = useSelector((state) => state?.appState?.isCustomApp);
  const changedDynamicFormValues = { ...dynamicFormValues };
  delete changedDynamicFormValues?.comments;
  delete changedDynamicFormValues?.hold_hours;
  delete changedDynamicFormValues?.offerTotalPrice;
  delete changedDynamicFormValues?.payWithin;
  delete changedDynamicFormValues?.radioSelect;
  delete changedDynamicFormValues?.offerTotalDiscount;
  delete changedDynamicFormValues?.recipientName;
  delete changedDynamicFormValues?.recipientEmail;
  delete changedDynamicFormValues?.countryCode;
  delete changedDynamicFormValues?.phoneNumber;
  const accountId = changedDynamicFormValues?.account_id;
  delete changedDynamicFormValues?.account_id;
  const customerAttributes: Record<string, any> = {};

  Object.keys(changedDynamicFormValues).forEach((key) => {
    const label =
      formData.find((field: any) => field.name === key)?.label ?? "";
    if (key === "offerTotalPrice") {
      customerAttributes[label] = currency(changedDynamicFormValues[key], {
        symbol: userCurrency,
        precision: isCustomApp
          ? userInfo?.roundUpPrices() && !isMeleeItem(item)
            ? 0
            : 2
          : 0,
      }).format();
    } else {
      customerAttributes[label] = changedDynamicFormValues[key];
    }
  });
  if (accountId) {
    customerAttributes["Account Id"] = accountId;
  }
  return customerAttributes;
};

export const useHideNavsOnScroll = () => {
  const yOffset = useSharedValue(0);

  const scrollHandler = useAnimatedScrollHandler((event) => {
    const { y } = event.contentOffset;

    if (y < 0 || y > event.contentSize.height) {
      return;
    }

    yOffset.value = y;
  });

  return { scrollHandler, yOffset };
};

export const getSelectedItemIds = (items: any) => {
  if (items && Array.isArray(items)) {
    return items
      .map((item) => item?.cart_item_id)
      .filter((id) => id !== undefined);
  }
  return [];
};

export const buttonDisabled = (items: any) => {
  return items?.length > 0;
};

export const filterSummaryData = (options, currentPriceMode) => {
  return options
    ?.filter((option) => option?.price_mode?.includes(currentPriceMode))
    ?.sort((a, b) => a.order - b.order);
};

export const getShowEditIcon = (bulkData: any, price_mode) => {
  return (
    bulkData?.price_modes?.includes(price_mode) && bulkData?.visible === true
  );
};

export const filterButtons = ({
  buttons,
  enabledQuote,
  priceMode,
  theme,
}: any) => {
  if (buttons && Object.keys(buttons).length) {
    const buttonsArray = Object.keys(buttons)
      .filter((button) =>
        enabledQuote && PriceModes.WHITE === priceMode
          ? button === "quote"
          : buttons[button].price_modes.length > 0 &&
            buttons[button].price_modes.includes(priceMode) &&
            buttons[button].acceptedScreenSizes.includes(
              theme.isLargeScreen
                ? ScreenSizes.LARGE
                : theme.isTablet
                ? ScreenSizes.TABLET
                : ScreenSizes.MOBILE
            ) &&
            button !== "quote"
      )
      ?.sort((a, b) => buttons[a].order - buttons[b].order)
      .map((button) => {
        return {
          ...buttons[button],
          label: buttons[button].label,
        };
      });
    return buttonsArray;
  }
};

export const getHeaderTitle = (
  productType: ProductCategory,
  stoneType: StoneType,
  t: any
) => {
  switch (productType) {
    case ProductCategory.DIAMOND:
      if (stoneType === StoneType.PAIR) {
        return t("bulkList.diamond.pair.text", "Natural Diamonds Pairs");
      } else if (stoneType === StoneType.PARCELS) {
        return t("bulkList.diamond.parcels.text", "Natural Diamonds Parcels");
      } else {
        return t("bulkList.diamond.singleStones.text", "Natural Diamonds");
      }
    case ProductCategory.GEMSTONE:
      if (stoneType === StoneType.PAIR) {
        return t("bulkList.gemstone.pair.text", "Colored Gemstones Pairs");
      } else if (stoneType === StoneType.PARCELS) {
        return t("bulkList.gemstone.parcels.text", "Colored Gemstones Parcels");
      } else {
        return t("bulkList.gemstone.singleStones.text", "Colored Gemstones");
      }
    case ProductCategory.JEWELRY:
      return t("bulkList.jewelry.singleStones.text", "Jewelry");
    case ProductCategory.LAB_GROWN_DIAMOND:
      if (stoneType === StoneType.PAIR) {
        return t("bulkList.labGrown.pair.text", "Lab-Grown Diamonds Pairs");
      } else if (stoneType === StoneType.PARCELS) {
        return t(
          "bulkList.labGrown.parcels.text",
          "Lab-Grown Diamonds Parcels"
        );
      } else {
        return t("bulkList.labGrown.singleStones.text", "Lab-Grown Diamonds");
      }
    default:
      return t("bulkList.product.text");
  }
};

export const useVendorGroupedBulkList = () => {
  const { isVDBOrg } = useOrgSettingsSlice();
  const theme = useTheme();
  return Boolean(isVDBOrg && (theme.isLargeScreen || theme.isTablet));
};

export const getBadgeCount = (sectionName: string, bulkItemData: any[]) => {
  const item = bulkItemData.find(
    (b) => (b.product_sub_type ?? "single_stones") === sectionName
  );

  return item?.total_count ?? "0";
};
